import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './styles.module.scss';
import { faBookmark, faCar, faCheck, faCoins, faEye, faGavel, faListSquares, faMoneyBill, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useSocket } from '../../../Context/socket';
import axios from 'axios';

import errorMsg from '../../../Functions/errorMsg';
import returnTotalNumberShort from '../../../Functions/numbers/formatTotalNumberShort';

import { PulseLoader } from 'react-spinners'
import DonutChart from '../../../Charts/DonutChart';

export default function Dashboard() {

  const { socket, socketConnected, auctionSocket } = useSocket();
  const { data: session, status } = useSession();

  const [initialLoading, setInitialLoading] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    if(initialLoading !== null) return;
    if(dashboardData !== null) return;
    if(!session) return;
    if(status === 'loading') return;

    async function getDashboardData() {
        setInitialLoading(true);

        const result = await axios.post(`${window.location.origin}/api/auctionHub/dashboard`, {
          getRequestingID: session.user.token.id
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
        })

        if(result && result.status === 200) {
          const data = result.data;
          setDashboardData(data);
        }

        setInitialLoading(false);
    }

    getDashboardData();

  }, [session, status])

  function isLoading() {
    if(status === 'loading') return true;
    if(initialLoading === null) return true;
    if(dashboardData === null) return true;
    return false;
  }

  const [statusReportExpanded, setStatusReportExpanded] = useState(false);

  return (
    <section id={styles.wrapper}>

      { isLoading() === true && <section id={styles.loading}><PulseLoader size={30} color='#154734' /></section> }

      { isLoading() === false && <>
        <section id={styles.mobileInfoContainer}>

          <div style={{ display: !statusReportExpanded ? 'flex' : 'none' }} onClick={() => setStatusReportExpanded(true)} className={styles.statusChart}>
            <h1>Status Report</h1>
             <div>
              <DonutChart chartData={dashboardData.statusReportChartData} />
            </div>
            <section style={{width: '100%', height: '100%', zIndex: '1', position: 'absolute'}}></section>
          </div>

          <div style={{ display: statusReportExpanded ? 'flex' : 'none', gridColumn: 'span 2', height: '400px' }} className={styles.statusChart}>
            <FontAwesomeIcon onClick={() => setStatusReportExpanded(false)} icon={faXmark} id={styles.exit} />
            <div>
              <DonutChart chartData={dashboardData.statusReportChartData} />
            </div>
          </div>

          <div className={styles.infoBox}>
            <div>
              <FontAwesomeIcon icon={faGavel} id={styles.icon} />
              <h1>Active</h1>
            </div>
            <div>
              <h1>{returnTotalNumberShort(dashboardData.insights.active)}</h1>
            </div>
          </div>

          <div className={styles.infoBox}>
            <div>
              <FontAwesomeIcon icon={faCar} id={styles.icon} />
              <h1>Pending</h1>
            </div>
            <div>
              <h1>{returnTotalNumberShort(dashboardData.insights.pending)}</h1>
            </div>
          </div>

          <div className={styles.infoBox}>
            <div>
              <FontAwesomeIcon icon={faCheck} id={styles.icon} />
              <h1>Completed</h1>
            </div>
            <div>
              <h1>{returnTotalNumberShort(dashboardData.insights.completed)}</h1>
            </div>
          </div>

          <div className={styles.infoBox}>
            <div>
              <FontAwesomeIcon icon={faEye} id={styles.icon} />
              <h1>Views</h1>
            </div>
            <div>
              <h1>{returnTotalNumberShort(dashboardData.insights.views)}</h1>
            </div>
          </div>

          <div className={styles.infoBox}>
            <div>
              <FontAwesomeIcon icon={faBookmark} id={styles.icon} />
              <h1>Saves</h1>
            </div>
            <div>
              <h1>{returnTotalNumberShort(dashboardData.insights.watchers)}</h1>
            </div>
          </div>

          <div className={styles.infoBox}>
            <div>
              <FontAwesomeIcon icon={faCoins} id={styles.icon} />
              <h1>All Bids</h1>
            </div>
            <div>
              <h1>{returnTotalNumberShort(dashboardData.insights.bids)}</h1>
            </div>
          </div>

          <div className={styles.infoBox}>
            <div>
              <FontAwesomeIcon icon={faMoneyBill} id={styles.icon} />
              <h1>Reserves</h1>
            </div>
            
            <div>
              <h1>${returnTotalNumberShort(dashboardData.insights.reserves)}</h1>
            </div>
          </div>

        </section>

        <section id={styles.desktopInfoContainer}>
          <section className={styles.gridSection}>
            <div className={styles.bigContainer}>
              <h1>Status Report</h1>
              <div>
                <DonutChart chartData={dashboardData.statusReportChartData} />
              </div>
            </div>
            <div className={styles.smallContainer}>
              <FontAwesomeIcon icon={faMoneyBill} id={styles.icon} />
              <h1>Reserves</h1>
              <h2>${returnTotalNumberShort(dashboardData.insights.reserves)}</h2>
            </div>
            <div className={styles.smallContainer}>
              <FontAwesomeIcon icon={faCoins} id={styles.icon} />
              <h1>All Bids</h1>
              <h2>{returnTotalNumberShort(dashboardData.insights.bids)}</h2>
            </div>
          </section>
          <section className={styles.gridSection}>
            <div className={styles.smallContainer}>
              <FontAwesomeIcon icon={faBookmark} id={styles.icon} />
              <h1>Saves</h1>
              <h2>{returnTotalNumberShort(dashboardData.insights.watchers)}</h2>
            </div>
            <div className={styles.smallContainer}>
              <FontAwesomeIcon icon={faGavel} id={styles.icon} />
              <h1>Active</h1>
              <h2>{returnTotalNumberShort(dashboardData.insights.active)}</h2>
            </div>
            <div className={styles.smallContainer}>
              <FontAwesomeIcon icon={faCar} id={styles.icon} />
              <h1>Pending</h1>
              <h2>{returnTotalNumberShort(dashboardData.insights.pending)}</h2>
            </div>
            <div className={styles.smallContainer}>
              <FontAwesomeIcon icon={faCheck} id={styles.icon} />
              <h1>Completed</h1>
              <h2>{returnTotalNumberShort(dashboardData.insights.completed)}</h2>
            </div>
          </section>


        </section>
      </>
      }
    </section>
  )
}

/*

        <div>
          <FontAwesomeIcon icon={faMoneyBill} id={styles.icon} />
          <span>
            <h1>Bid Reserves</h1>
            <h2>${parseFloat(dashboardData.insights.reserves).toLocaleString()}</h2>
          </span>
        </div>
*/